import { ChangeEvent } from 'react';
import { InputLabel, MenuItem, Select, SelectChangeEvent, TextField, FormControl, FormHelperText } from '@mui/material';
import { CoreBox } from '@youscience/core';
import { GpaWeighting, RegGpaScale, YesNo } from '@youscience/commonapp-service-common';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';

import SectionAccordion from '@components/SectionAccordion';
import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import MonthYearDatePicker from '@components/MonthYearDatePicker';
import ContinueButton from '@components/ContinueButton';

import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import { StyledCard } from '@pages/RecommenderSpace/components/Card.styles';

import { maxDate, minDate } from '../../../constants';
import { SchoolDetailsProps, GPAFields } from '../../types';
import { FormState } from '@constants/commonApp/types';
import useFormSection from '@pages/RecommenderSpace/hooks/useFormSection';
import { dateRangeValidation } from '@pages/StudentDetailsPage/components/common/formUtils';

export const GPA = ({ onExpand, onSaveAndContinue, isExtended, isSaved }: SchoolDetailsProps<GPAFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const {
    initialState: { gpa: initialState },
    formState: { gpa: formState },
  } = useRecommenderProfileStore((store) => store);

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
  const isFilledAllRequiredFields =
    formState.data.reporting === YesNo.No ||
    (formState.data.scale &&
      formState.data.startDate &&
      formState.data.endDate &&
      formState.data.weighting &&
      formState.data.passing &&
      formState.data.highest);

  const initializeValues = () => {
    const { reporting, scale, weighting, startDate, endDate, passing, highest } =
      recommenderProfile?.schoolProfile?.gpa ?? {};

    return {
      reporting: reporting,
      scale: scale,
      weighting: weighting,
      startDate: startDate,
      endDate: endDate,
      passing: passing,
      highest: highest ?? undefined,
    };
  };

  const additionalValidations = (newFormState: FormState<GPAFields>) => {
    return dateRangeValidation(newFormState, {});
  };

  const { updateFormValues, handleDateChange, handleRadioChange, handleDateError } = useFormSection<GPAFields>({
    initialState,
    formState: formState,
    initializeValues,
    additionalValidations,
    sectionName: SectionName.GPA,
  });

  const handleFieldChange = (field: string, value: string | number | undefined) => {
    let updates: Partial<GPAFields> = { [field]: value };

    if (field === 'reporting' && value === '1') {
      updates = {
        ...updates,
        scale: undefined,
        startDate: undefined,
        endDate: undefined,
        weighting: undefined,
        passing: undefined,
        highest: undefined,
      };
    }

    updateFormValues(updates);
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;

    handleFieldChange(field, value);
  };

  const handleRegexFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const regex = /^([a-zA-Z]+)$|^([0-9]+?.[0-9]+)$|^([0-9]+)$/;

    if ((regex.test(value) && value.length <= 6) || (value.length <= 5 && value.endsWith('.')) || value === '') {
      handleFieldChange(event.target.name, value);

      formState.errors[event.target.name as keyof typeof formState.errors] = '';
    }
  };

  return (
    <SectionAccordion
      title={'GPA'}
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      isError={isShowErrors && !isFilledAllRequiredFields}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <RadioButtonGroupWithClearButton
            title="Do you report GPA? (Grade Point Average)"
            name="reporting"
            value={formState.data.reporting ?? undefined}
            options={YesNo}
            required
            error={isShowErrors && !formState.data.reporting}
            helperText={isShowErrors && !formState.data.reporting ? REQUIRED_FIELD_MESSAGE : ''}
            handleRadioChange={handleRadioChange}
            handleClear={() =>
              updateFormValues({
                reporting: undefined,
                weighting: undefined,
                scale: undefined,
                startDate: undefined,
                endDate: undefined,
                passing: undefined,
                highest: undefined,
              })
            }
          />

          {formState.data.reporting === '0' ? (
            <>
              <FormControl size="small" sx={{ width: '200px' }} error={isShowErrors && !formState.data.scale}>
                <InputLabel required>GPA scale</InputLabel>

                <Select
                  data-testid="scale"
                  label="GPA scale *"
                  value={formState.data.scale ?? ''}
                  onChange={handleSelectChange('scale')}
                >
                  {Object.entries(RegGpaScale).map(([key, value]) => {
                    return (
                      <MenuItem key={value} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </Select>

                {isShowErrors && !formState.data.scale && <FormHelperText>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
              </FormControl>

              <CoreBox sx={{ display: 'flex', gap: '1.5rem', marginTop: '.5rem', alignItems: 'start' }}>
                <MonthYearDatePicker
                  name="startDate"
                  label="From (start date)"
                  required
                  slotProps={{
                    inputProps: { clearable: true, onClear: () => handleDateChange('startDate') },
                    error: (isShowErrors && !formState.data.startDate) || Boolean(formState.errors['startDate']),
                  }}
                  value={formState.data.startDate ?? undefined}
                  error={
                    isShowErrors && !formState.data.startDate ? REQUIRED_FIELD_MESSAGE : formState.errors.startDate
                  }
                  handleDateChange={handleDateChange('startDate')}
                  handleDateError={handleDateError}
                  minDate={minDate}
                  maxDate={maxDate}
                />

                <MonthYearDatePicker
                  name="endDate"
                  label="Ending (final date)"
                  required
                  slotProps={{
                    inputProps: { clearable: true, onClear: () => handleDateChange('endDate') },
                    error: (isShowErrors && !formState.data.endDate) || Boolean(formState.errors['endDate']),
                  }}
                  value={formState.data.endDate ?? undefined}
                  error={isShowErrors && !formState.data.endDate ? REQUIRED_FIELD_MESSAGE : formState.errors.endDate}
                  handleDateChange={handleDateChange('endDate')}
                  handleDateError={handleDateError}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </CoreBox>

              <RadioButtonGroupWithClearButton
                title="GPA weighting"
                name="weighting"
                value={formState.data.weighting ?? undefined}
                options={GpaWeighting}
                required
                error={isShowErrors && !formState.data.weighting}
                helperText={isShowErrors && !formState.data.weighting ? REQUIRED_FIELD_MESSAGE : ''}
                handleRadioChange={handleRadioChange}
                handleClear={() => updateFormValues({ weighting: undefined })}
              />

              <CoreBox sx={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                <FormControl fullWidth>
                  <TextField
                    name="passing"
                    label="School's passing mark"
                    value={formState.data.passing ?? ''}
                    required
                    error={isShowErrors && !formState.data.passing}
                    helperText={isShowErrors && !formState.data.passing ? REQUIRED_FIELD_MESSAGE : ''}
                    onChange={handleRegexFieldChange}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    name="highest"
                    label="Highest grade/GPA in class"
                    value={formState.data.highest ?? ''}
                    required
                    error={isShowErrors && !formState.data.highest}
                    helperText={isShowErrors && !formState.data.highest ? REQUIRED_FIELD_MESSAGE : ''}
                    onChange={handleRegexFieldChange}
                  />
                </FormControl>
              </CoreBox>
            </>
          ) : null}

          <ContinueButton onContinue={onSaveAndContinue} isDisabled={!formState.isFormValid} />
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
